/* eslint-disable @typescript-eslint/no-explicit-any */
// import Cookies from 'js-cookie';

const localStorageNode = 'vg-admin-auth';
const localStoragePhoneNode = 'vg-admin-phone';
const localStorageHasEmailNode = 'vg-admin-has-email';
/**
 * This browser's own FCM token.
 *
 * Deliberately outside `vg-admin-auth`: a user can be signed in on several
 * devices, so which token belongs to THIS one is a device fact, not a user
 * fact - and logout clears the user while the token has to survive long enough
 * to be sent with `POST /v1/users/logout`, which is what stops the other
 * devices from being unsubscribed too.
 */
const localStorageDeviceTokenNode = 'vg-device-fcm-token';

// SSR-safe guards: these helpers are reachable at module scope through the
// @vgrubs/components barrel, which the tracker app evaluates during Next.js
// prerendering where localStorage doesn't exist.
export const store = (key: string, value: any) => {
  if (typeof localStorage === 'undefined') return;
  return localStorage.setItem(key, value);
};

export const read = (key: string) => {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(key);
};

export const getTokens = () => {
  return JSON.parse(read(localStorageNode) as string)?.state?.tokens;
};

export const getAccessToken = () => {
  return JSON.parse(read(localStorageNode) as string)?.state?.tokens
    ?.accessToken;
};

export const getRefreshToken = () => {
  return JSON.parse(read(localStorageNode) as string)?.state?.tokens
    ?.refreshToken;
};

export const getUserFromLS = () => {
  return JSON.parse(read(localStorageNode) as string)?.state?.user;
};

/**
 * The phone number this browser last signed in with, so the login field can
 * offer it instead of opening blank. Guarded because a malformed value must
 * not take the login page down with it - the field simply starts empty.
 */
export const getPhoneFromLS = (): string | null => {
  try {
    return JSON.parse(read(localStoragePhoneNode) as string);
  } catch {
    return null;
  }
};

export const getHasEmailFromLS = () => {
  return JSON.parse(read(localStorageHasEmailNode) as string);
};

/**
 * Read synchronously at logout, when the auth store is about to be cleared and
 * an async `getToken()` round-trip would race the navigation away.
 */
export const getDeviceFcmToken = () => read(localStorageDeviceTokenNode);

export const setDeviceFcmToken = (token: string) =>
  store(localStorageDeviceTokenNode, token);

export const clearDeviceFcmToken = () => {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(localStorageDeviceTokenNode);
};

export const setNewAccessToken = (accessToken: string) => {
  const data = JSON.parse(read(localStorageNode) as string);
  const newToken = {
    ...data,
    state: { ...data.state, tokens: { ...data.state.tokens, accessToken } },
  };
  store(localStorageNode, JSON.stringify(newToken));
};

export const setLoginDataToLS = (data: any) => {
  store(localStorageNode, JSON.stringify(data));
};

export const setPhoneNumberLS = (data: any) => {
  store(localStoragePhoneNode, JSON.stringify(data));
};

export const setHasEmailLS = (data: any) => {
  store(localStorageHasEmailNode, JSON.stringify(data));
};

export const removeUserDataFromLS = () => {
  localStorage.removeItem('vgrubs-admin-closing-days-location');
  localStorage.removeItem('vgrubs-admin-deliveries-brands');
  localStorage.removeItem('vgrubs-admin-deliveries-location');
  localStorage.removeItem('vgrubs-admin-orders-brands');
  localStorage.removeItem('vgrubs-admin-orders-location');
  localStorage.removeItem('vgrubs-admin-settings-brands-location');
  localStorage.removeItem('vgrubs-admin-settings-location');
  localStorage.removeItem('vgrubs-admin-working-hours-location');
  localStorage.removeItem('vgrubs-partner-v-drive-location');
};
